import { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { v4 as uuidv4 } from 'uuid';
import { useDispatch } from "react-redux";

import { Button, CalendarField, DropDownField, InputSwitchField, TextAreaField, TextInputField } from "../../../../helpers";

import PositiveAdjDisplay from "./PositiveAdjDisplay";
import NegativeAdjDisplay from "./NegativeAdjDisplay";
import Loading from "../../../../common/Loading";

import { searchSmartStockMoves } from "../../../../services/api/smartStockMoves";
import { raiseToast } from "../../../../services/redux/reducers/toast/toast";
import { getStockWarehouses } from "../../../../services/api/stockWarehouse";
import { getSellerPOItemsByDocNos } from "../../../../services/api/sellerPOItem";
import { insertStockAdjustment } from "../../../../services/api/stockAdjustment";

import { validateInputs } from "../../../../utils";

import { newStockAdjustmentInitialValues, newStockAdjustmentValidationSchema } from "../../../../validation/stockAdjustment";
import { stockWarehousesParser } from "../../../../parserFunctions";


const AddStockAdjustmentNew = (props) => {

    const { companyWarehouses, GSTRates, setNewVisibleAddStockAdjustment } = props

    const [stockAdjustment, setStockAdjustment] = useState(newStockAdjustmentInitialValues)
    const [stockWarehouse, setStockWarehouse] = useState(null)
    const [sellerPOs, setSellerPOs] = useState([])
    
    const [formErrors, setFormErrors] = useState([])

    const [isLoading, setIsLoading] = useState(false)


    const dispatch = useDispatch()

    useEffect(() => {
        if(stockAdjustment.isPositiveAdjustment && sellerPOs?.length === 0){
            setSellerPOs([{
                tempSellerPOId: uuidv4(),
                sellerPODocNo: null,
                unitPrice: null,
                GSTRate: null,
                sellerPOItemQty: null,
                stockWarehouseQty: null,
                isValidSellerPO: false,
                adjustedQty: null
            }])
        }
        if(!stockAdjustment.isPositiveAdjustment && sellerPOs?.length){
            setSellerPOs([])
        }
    }, [])

    useEffect(() => {
        if(stockAdjustment.isPositiveAdjustment){
            setSellerPOs([{
                tempSellerPOId: uuidv4(),
                sellerPODocNo: null,
                unitPrice: null,
                GSTRate: null,
                sellerPOItemQty: null,
                stockWarehouseQty: null,
                isValidSellerPO: false,
                adjustedQty: null
            }])
        }
        if(!stockAdjustment.isPositiveAdjustment){
            setSellerPOs([])
        }
        setStockAdjustment({...stockAdjustment, moveDate: null, remarks: null})
        setStockWarehouse(null)
    }, [stockAdjustment.materialId, stockAdjustment.warehouseId, stockAdjustment.isPositiveAdjustment])

    const handleStockAdjustmentChange = (e) => {
        const updatedStockAdjustment = { ...stockAdjustment, [e.target.name]: e.target.value }
        setStockAdjustment({ ...updatedStockAdjustment })
    }

    const handleStockDetailsClick = async () => {

        if (!stockAdjustment.materialId) {
            return dispatch(raiseToast({ severity: 'warn', detail: 'Please enter material ID!', life: 3000 }))
        }

        if (stockAdjustment.materialId.length <= 5) {
            return dispatch(raiseToast({ severity: 'warn', detail: 'The length of material ID is at least 5!', life: 3000 }))
        }

        if (!stockAdjustment.warehouseId) {
            return dispatch(raiseToast({ severity: 'warn', detail: 'Please enter warehouse ID!', life: 3000 }))
        }

        setIsLoading(true)

        const stockWarehouseRes = await getStockWarehouses({ materialIds: [parseInt(stockAdjustment.materialId)], warehouseIds: [stockAdjustment.warehouseId], statusIds: [1], paginator: false })
        if (stockWarehouseRes.failure) {
            setIsLoading(false)
            return dispatch(raiseToast({ severity: 'error', summary: "Smart Stock Moves", detail: stockWarehouseRes.data.error, life: 3000 }))
        }

        if (stockAdjustment.isPositiveAdjustment) {
            if(stockWarehouseRes.data.data.length){
                let stockWarehouseData = stockWarehousesParser(stockWarehouseRes.data.data)
                setStockWarehouse(stockWarehouseData)
            }
            else{
                setStockWarehouse({
                    smartStock: 0
                })
            }
        } else {

            const smartStockMovesRes = await searchSmartStockMoves({ materialIds: [parseInt(stockAdjustment.materialId)], warehouseIds: [stockAdjustment.warehouseId], statusIds: [1], paginator: false })
            if (smartStockMovesRes.failure) {
                setIsLoading(false)
                return dispatch(raiseToast({ severity: 'error', summary: "Smart Stock Moves", detail: smartStockMovesRes.data.error, life: 3000 }))
            }

            if (smartStockMovesRes.data.data.length) {
                const newSellerPOs = smartStockMovesRes.data.data.map(eachSmartStockMove => {
                    return {
                        tempSellerPOId: uuidv4(),
                        sellerPODocNo: eachSmartStockMove.sellerPODocNo,
                        unitPrice: eachSmartStockMove.price,
                        GSTRate: eachSmartStockMove.GSTRate,
                        sellerPOItemQty: eachSmartStockMove.moveQty,
                        stockWarehouseQty: null,
                        isValidSellerPO: true,
                        adjustedQty: null
                    }
                })

                setSellerPOs([...newSellerPOs])
                setStockWarehouse(stockWarehouseRes.data.data[0])
            } else {
                setIsLoading(false)
                return dispatch(raiseToast({ severity: 'warn', detail: 'Stock details are not found!', life: 3000 }))
            }
        }
        setIsLoading(false)
    }

    const handleAdjustmentType = (e) => {

        if (e.value) {
            setSellerPOs([{
                tempSellerPOId: uuidv4(),
                sellerPODocNo: null,
                unitPrice: null,
                GSTRate: null,
                sellerPOItemQty: null,
                stockWarehouseQty: null,
                isValidSellerPO: false,
                adjustedQty: null
            }])
        }
        else {
            setSellerPOs([])
        }

        const updatedStockAdjustment = { ...stockAdjustment, isPositiveAdjustment: e.value }
        setStockAdjustment({ ...updatedStockAdjustment })
    }

    const handleSellerPOsChange = (id, name, value) => {
        const updatedSellerPOs = sellerPOs.map(eachSelPO => {
            if(eachSelPO.tempSellerPOId === id){
                return {...eachSelPO, [name]: value}
            }
            return eachSelPO
        })

        setSellerPOs([...updatedSellerPOs])
    }

    const handleOnHideAddAdjustment = () => {
        setSellerPOs([])
        setStockWarehouse(null)
        setStockAdjustment(newStockAdjustmentInitialValues)
        setNewVisibleAddStockAdjustment(false)
    }

    const handleSellerPOItem = async (id, docNo) => {
        if(!docNo){
            return dispatch(raiseToast({ severity: 'error', detail: 'Please enter seller PO Doc No!', life: 3000 }))
        }

        if(docNo.length !== 15){
            return dispatch(raiseToast({ severity: 'error', detail: 'Seller PO length should be 15!', life: 3000 }))
        }

        setIsLoading(true)
        const sellerPOItemRes = await getSellerPOItemsByDocNos({docNos: [docNo]})
        if (sellerPOItemRes.failure) {
            setIsLoading(false)
            return dispatch(raiseToast({ severity: 'error', summary: "Seller PO Item", detail: sellerPOItemRes.data.error, life: 3000 }))
        }
        
        if(sellerPOItemRes.data.length === 0){
            setIsLoading(false)
            return dispatch(raiseToast({ severity: 'warn', detail: 'No seller PO is found with this material', life: 3000 }))
        }
        if(sellerPOItemRes.data.length){
            const relatedSellerPOItem = sellerPOItemRes.data.find(eachPOItem => eachPOItem.materialId === parseInt(stockAdjustment.materialId))

            if (!relatedSellerPOItem) {
                setSellerPOs([{
                    tempSellerPOId: uuidv4(),
                    unitPrice: null,
                    GSTRate: null,
                    sellerPOItemQty: null,
                    stockWarehouseQty: null,
                    isValidSellerPO: false,
                    adjustedQty: null
                }])
                setIsLoading(false)
                return dispatch(raiseToast({ severity: 'warn', detail: 'No seller PO is found with this material', life: 3000 }))
            }
            else{
                const sellerPOItem = sellerPOs[0]
                setSellerPOs([{
                    tempSellerPOId: sellerPOItem.tempSellerPOId,
                    sellerPODocNo: sellerPOItem.sellerPODocNo,
                    unitPrice: relatedSellerPOItem.unitPrice,
                    GSTRate: relatedSellerPOItem.GSTRate,
                    sellerPOItemQty: relatedSellerPOItem.qty,
                    isValidSellerPO: true,
                    adjustedQty: null
                }])
            }
        }

        setIsLoading(false)
    }

    const handleSubmitClick = async () => {

        const validationRes = await validateInputs(newStockAdjustmentValidationSchema, stockAdjustment)
        if (validationRes) {
            if (validationRes.error) {
                setFormErrors(validationRes.error)
            }
            else {
                setFormErrors({})

                if(!stockWarehouse){
                    return dispatch(raiseToast({ severity: 'error', detail: `Please get stock details`, life: 3000 }))
                }

                let stockAdjustments = []
                if(validationRes.result.isPositiveAdjustment){
                    const isDataNotProvided = sellerPOs.some(eachPO => {
                        if(eachPO.sellerPODocNo && !eachPO.isValidSellerPO){
                            dispatch(raiseToast({ severity: 'error', detail: `Please verify seller PO Doc no`, life: 3000 }))
                            return true
                        }
                        if (!eachPO.unitPrice) {
                            dispatch(raiseToast({ severity: 'error', detail: `Please enter price`, life: 3000 }))
                            return true
                        }
                        if (!eachPO.GSTRate && eachPO.GSTRate !== 0) {
                            dispatch(raiseToast({ severity: 'error', detail: `Please enter GST rate`, life: 3000 }))
                            return true
                        }
                        if (!eachPO.adjustedQty) {
                            dispatch(raiseToast({ severity: 'error', detail: `Please enter quantity to adjust`, life: 3000 }))
                            return true
                        }
                    })

                    if(isDataNotProvided){
                        return
                    }

                    stockAdjustments = sellerPOs.map(eachPO => {
                        return{
                            materialId: parseInt(stockAdjustment.materialId),
                            warehouseId: stockAdjustment.warehouseId,
                            isPositiveAdjustment: stockAdjustment.isPositiveAdjustment,
                            sellerPODocNo: eachPO.sellerPODocNo,
                            sellerPONo: eachPO.sellerPODocNo,
                            unitPrice: parseFloat(eachPO.unitPrice),
                            GSTRate: eachPO.GSTRate,
                            adjustedQty: parseFloat(eachPO.adjustedQty),
                            moveDate: stockAdjustment.moveDate,
                            remarks: stockAdjustment.remarks
                        }
                    })
                }
                else{
                    if(sellerPOs.length === 0){
                        return dispatch(raiseToast({ severity: 'error', detail: `Please get stock details`, life: 3000 }))
                    }

                    const filteredSellerPOs = sellerPOs.filter(eachPO => parseFloat(eachPO.adjustedQty) > 0)

                    if(filteredSellerPOs.length === 0){
                        return dispatch(raiseToast({ severity: 'error', detail: `Please enter quantity to adjust`, life: 3000 }))
                    }

                    const isDataNotProvided = filteredSellerPOs.some(eachPO => {
                        if (!eachPO.sellerPODocNo) {
                            dispatch(raiseToast({ severity: 'error', detail: `Please provide Seller PO for ${stockAdjustment.materialId}`, life: 3000 }))
                            return true
                        }
                        if (!eachPO.unitPrice) {
                            dispatch(raiseToast({ severity: 'error', detail: `Please give price against Seller PO ${eachPO.sellerPODocNo}`, life: 3000 }))
                            return true
                        }
                        if (!eachPO.GSTRate && eachPO.GSTRate !== 0) {
                            dispatch(raiseToast({ severity: 'error', detail: `Please give GST rate against Seller PO ${eachPO.sellerPODocNo}`, life: 3000 }))
                            return true
                        }
                    })

                    if(isDataNotProvided){
                        return
                    }

                    stockAdjustments = filteredSellerPOs.map(eachPO => {
                        return{
                            materialId: parseInt(stockAdjustment.materialId),
                            warehouseId: stockAdjustment.warehouseId,
                            isPositiveAdjustment: stockAdjustment.isPositiveAdjustment,
                            sellerPODocNo: eachPO.sellerPODocNo,
                            sellerPONo: eachPO.sellerPODocNo,
                            unitPrice: parseFloat(eachPO.unitPrice),
                            GSTRate: eachPO.GSTRate,
                            adjustedQty: parseFloat(eachPO.adjustedQty),
                            moveDate: stockAdjustment.moveDate,
                            remarks: stockAdjustment.remarks
                        }
                    })
                }

                if (window.confirm("Are you sure you want to continue?")) {
                    setIsLoading(true)
                    const stockAdjustmentRes = await insertStockAdjustment({ stockAdjustments: stockAdjustments })

                    if (stockAdjustmentRes.failure) {
                        setIsLoading(false)
                        return dispatch(raiseToast({ severity: 'error', summary: 'Stock Adjustment', detail: stockAdjustmentRes.data.error, life: 3000 }))
                    }
                    setIsLoading(false)
                    handleOnHideAddAdjustment()
                }
            }
        }
    }

    //change to sellerPODocNo in all places(including material manufacturing)
    const adjustmentMinDate = new Date()
    adjustmentMinDate.setDate(new Date().getDate() - 10)
    const adjustmentMaxDate = new Date()

    const disableCond = sellerPOs.some(eachPO => {
        if(eachPO?.sellerPOCompanyId === 1){
            return true
        }
        return false
    })

    const positiveAdjDisplayCond = stockAdjustment.isPositiveAdjustment && stockWarehouse
    const negativeAdjDisplayCond = !stockAdjustment.isPositiveAdjustment && sellerPOs.length > 0 && stockWarehouse 

    return (
        <Dialog header="Stock Adjustment" visible={props.visible} style={{ minHeight: "60%", width: '60vw', }} onHide={handleOnHideAddAdjustment}>
            <Loading visible={isLoading} />
            <div className="flex flex-row">
                <div className="flex flex-column col-12 mb-2 lg:col-3 lg:mb-0">
                    <TextInputField name="materialId" labelText="Material ID" value={stockAdjustment.materialId} onChange={handleStockAdjustmentChange} errorMessage={formErrors.materialId} keyfilter={"pnum"} required />
                </div>
                <div className="flex flex-column col-12 mb-2 lg:col-3 lg:mb-0">
                    <DropDownField name="warehouseId" labelText="Warehouse" options={companyWarehouses} value={stockAdjustment.warehouseId} optionLabel="warehouseName" optionValue="warehouseId" onChange={(e) => handleStockAdjustmentChange(e)} errorMessage={formErrors.warehouseId} required />
                </div>
                <div className="flex flex-column col-12 lg:col-2 lg:mb-0 pr-0">
                    <label className='adjustment-type-label'>Adjustment Type</label>
                    <div className='flex flex-row  align-items-center mt-2'>
                        <span className='mr-1 text-red-500'>-ve</span>
                        <span className='mt-1'><InputSwitchField className="" name="isPositiveAdjustment" checked={stockAdjustment.isPositiveAdjustment} onChange={(e) => handleAdjustmentType(e)} /></span>
                        <span className='ml-1 text-green-500'>+ve</span>
                    </div>
                </div>
                <div className="flex align-items-center">
                    <Button label='Get Stock Details' className='h-2rem p-2' onClick={handleStockDetailsClick} text />
                </div>
            </div>
            <div className="flex flex-row">
                <div className="flex align-items-center">
                    <p className='text-red-500 ml-2'>{stockAdjustment.isPositiveAdjustment ? 'Note: Positive adjustment will add quantity to smart stock' : 'Note: Negative adjustment will decrease quantity in smart stock and add it as discard stock'}</p>
                </div>
            </div>
            {
                stockWarehouse ?
                <div className="flex align-items-center">
                    <p className="ml-2">Current stock in the warehouse: </p>
                    <div className="flex flex-column col-12 mb-2 lg:col-2 lg:mb-0">
                        <TextInputField name="currentStockQty" value={stockWarehouse?.smartStock ? stockWarehouse.smartStock : 0} errorMessage={formErrors.currentStockQty} keyfilter={"pnum"} disabled required />
                    </div>
                </div>
                    : null
            }
            <div className="flex">
                {positiveAdjDisplayCond &&
                    <PositiveAdjDisplay
                        sellerPOs={sellerPOs}
                        GSTRates={GSTRates}
                        handleSellerPOsChange={handleSellerPOsChange}
                        handleSellerPOItem={handleSellerPOItem}
                        isLoading={isLoading}
                    />}
                {
                    !positiveAdjDisplayCond && stockAdjustment.isPositiveAdjustment ? <p>Get Stock Details to proceed with positive adjustment</p> : null
                }
            </div>
            <div>
                {negativeAdjDisplayCond &&
                    <NegativeAdjDisplay
                        sellerPOs={sellerPOs}
                        GSTRates={GSTRates}
                        handleSellerPOsChange={handleSellerPOsChange}
                    />}
                {
                    !negativeAdjDisplayCond && !stockAdjustment.isPositiveAdjustment ? <p className="align-self-center">Get stock seller POs for the material to proceed with negative adjustment</p> : null
                }
            </div>
            <div className="flex flex-column col-12 lg:col-3 lg:mb-0">
                <CalendarField name="moveDate" dateFormat='yy-mm-dd' value={stockAdjustment.moveDate} minDate={adjustmentMinDate} maxDate={adjustmentMaxDate} onChange={handleStockAdjustmentChange} labelText="Adjustment Date" errorMessage={formErrors.moveDate} showIcon required />
            </div>
            <div>
                <div className="flex flex-column col-12 text-area">
                    <TextAreaField name="remarks" labelText="Remarks" rows={3} onChange={handleStockAdjustmentChange} value={stockAdjustment.remarks} errorMessage={formErrors.remarks} required maxLength={100} />
                </div>
            </div>
            <div className='flex justify-content-center mt-2'>
                <Button label="Submit" onClick={handleSubmitClick} disabled={disableCond} tooltip={disableCond ? `Can't do adjustments for useFromStock seller PO` : ''} tooltipOptions={{ position: 'top', showOnDisabled: true, }} />
            </div>
        </Dialog>
    )
}

export default AddStockAdjustmentNew